import { DEFAULT_LOCALE, getLocaleName } from "@redotech/redo-i18n/locales";
import { Divider } from "@redotech/redo-web/arbiter-components/divider/divider";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoTextareaInputField } from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { Flex } from "@redotech/redo-web/flex";
import {
  convertToQuillHTML,
  QuillEditor,
} from "@redotech/redo-web/quill/quill-editor";
import { Text } from "@redotech/redo-web/text";
import { ComponentRef, Fragment, useRef } from "react";
import { translationEditorKind } from "./translation-editor-kind";

/** A single piece of authored copy that can be translated per language. */
export interface TranslatableFieldSpec {
  field: string;
  kind: "text" | "html";
  /** Text fields authored in a textarea translate in a textarea too. */
  multiline?: boolean;
  label: string;
  source: string;
}

export function TranslationFieldInput({
  editorKey,
  field,
  language,
  value,
  onChange,
  disabled = false,
  fullWidth = false,
}: {
  editorKey: string;
  field: TranslatableFieldSpec;
  language: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  fullWidth?: boolean;
}) {
  switch (translationEditorKind(field)) {
    case "html":
      return (
        <TranslationHtmlEditor
          id={`${editorKey}-${field.field}-${language}`}
          onChange={onChange}
          readOnly={disabled}
          value={value}
        />
      );
    case "multiline":
      return (
        <RedoTextareaInputField
          autoExpand
          fullWidth={fullWidth}
          placeholder={field.source}
          readOnly={disabled}
          setValue={onChange}
          value={value}
        />
      );
    case "single-line":
      return (
        <RedoTextInput
          fullWidth={fullWidth}
          placeholder={field.source}
          setValue={onChange}
          state={disabled ? "readonly" : "default"}
          value={value}
        />
      );
  }
}

export function TranslationOriginalValue({
  editorKey,
  field,
}: {
  editorKey: string;
  field: TranslatableFieldSpec;
}) {
  switch (translationEditorKind(field)) {
    case "html":
      return (
        <QuillEditor
          defaultHtmlContents={convertToQuillHTML(field.source)}
          editorId={`xlate-original-${editorKey}-${field.field}`}
          editorStyles={{
            backgroundColor: "var(--redo-colors-background-bg-secondary)",
            border: "1px solid var(--redo-colors-border-border-secondary)",
            borderRadius: "var(--border-radius-xl)",
          }}
          readOnly
        />
      );
    case "multiline":
      return (
        <RedoTextareaInputField
          autoExpand
          fullWidth
          readOnly
          setValue={noop}
          value={field.source}
        />
      );
    case "single-line":
      return (
        <RedoTextInput
          fullWidth
          setValue={noop}
          state="readonly"
          value={field.source}
        />
      );
  }
}

function noop() {}

/** Field-major: each field shows its authored original, then one editor per language. */
export function TranslationFieldsEditor({
  editorKey,
  fields,
  languages,
  valueFor,
  setTranslation,
  disabled = false,
}: {
  editorKey: string;
  fields: TranslatableFieldSpec[];
  languages: string[];
  valueFor: (language: string, field: string) => string;
  setTranslation: (language: string, field: string, value: string) => void;
  disabled?: boolean;
}) {
  return (
    <>
      {fields.map((f, i) => (
        <Fragment key={f.field}>
          {i > 0 && <Divider />}
          <Flex dir="column" gap="lg">
            <Text fontSize="sm" fontWeight="semibold">
              {f.label}
            </Text>
            <Flex dir="column" gap="sm">
              <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                Original
              </Text>
              <TranslationOriginalValue editorKey={editorKey} field={f} />
            </Flex>
            <Flex dir="column" gap="3xl">
              {languages.map((language) => (
                <Flex dir="column" gap="sm" key={language}>
                  <Text fontSize="sm" fontWeight="semibold">
                    {getLocaleName(language, DEFAULT_LOCALE)}
                  </Text>
                  <TranslationFieldInput
                    disabled={disabled}
                    editorKey={editorKey}
                    field={f}
                    language={language}
                    onChange={(value) =>
                      setTranslation(language, f.field, value)
                    }
                    value={valueFor(language, f.field)}
                  />
                </Flex>
              ))}
            </Flex>
          </Flex>
        </Fragment>
      ))}
    </>
  );
}

export function TranslationHtmlEditor({
  id,
  value,
  onChange,
  readOnly = false,
}: {
  id: string;
  value: string;
  onChange: (html: string) => void;
  readOnly?: boolean;
}) {
  const quillRef = useRef<ComponentRef<typeof QuillEditor>>(null);
  const editorId = `xlate-editor-${id}`;
  const toolbarId = `xlate-toolbar-${id}`;
  return (
    <Flex dir="column" gap="xs">
      {/* Static, in-flow toolbar (no floating header). Fixed button sizing
          overrides Quill snow's float-based default so the row wraps cleanly
          in a narrow inspector panel. */}
      <style>
        {`#${toolbarId} {
            display: flex;
            flex-wrap: wrap;
            gap: var(--redo-spacing-xxs);
            padding: var(--redo-spacing-xs);
            background-color: var(--redo-colors-background-bg-secondary);
            border: 1px solid var(--redo-colors-border-border-primary);
            border-radius: var(--border-radius-lg);
          }
          #${toolbarId} button {
            width: 28px;
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            border: none;
            background: none;
            border-radius: var(--border-radius-md);
            cursor: pointer;
            padding: 0;
          }
          #${toolbarId} button:hover {
            background-color: var(--redo-colors-background-bg-primary_hover);
          }`}
      </style>
      <div id={toolbarId}>
        <button className="ql-bold" type="button" />
        <button className="ql-italic" type="button" />
        <button className="ql-underline" type="button" />
        <button className="ql-strike" type="button" />
        <button className="ql-align" type="button" value="" />
        <button className="ql-align" type="button" value="center" />
        <button className="ql-list" type="button" value="bullet" />
      </div>
      <QuillEditor
        defaultHtmlContents={convertToQuillHTML(value)}
        editorId={editorId}
        editorStyles={{
          border: "1px solid var(--redo-colors-border-border-secondary)",
          borderRadius: "var(--border-radius-lg)",
          minHeight: 72,
        }}
        onTextChange={() => {
          const html = quillRef.current?.getSemanticHTML();
          if (html != null && html !== value) {
            onChange(html);
          }
        }}
        placeholder="Write here…"
        readOnly={readOnly}
        ref={quillRef}
        toolbarId={toolbarId}
      />
    </Flex>
  );
}
